import { Port } from 'src/types/ViewModel/Port';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { PortCallStatus } from 'src/enums/portCallStatus';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { getBerthCalls, getVoyageNumberString } from '../../stowageRotationUtils';
import { PortCall } from 'src/types/ViewModel/PortCall';
import { RotationTreeEnum } from 'src/enums/rotationTreeEnum';
import { ReportLocationEnum } from 'src/enums/reportLocationEnum';
import { mapBerthCallToRotationBerthCallSimple, mapRotationBerthCallsToBerthCalls } from './berthCallUtils';
import { createEventDate } from './eventDateUtils';
import { emptyMoveActionMsg } from './moveActionMsgUtils';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { updateBerthCallInPortCluster } from 'src/factory/ReportEvents/Rotation/PortCallFactory';
import { TimeZone } from 'src/types/ViewModel/TimeZone';

export const mapPortCallToRotationPortCallSimple = (portCall: PortCall, imoNumber: number): RotationPortCall => {
    const rotationPortCall = {
        portCallGuid: crypto.randomUUID(),
        imosPortCallId: portCall.portCallId,
        portId: portCall.port.portId,
        imoNumber,
        voyageNumber: portCall.voyageNumber1,
        turnaroundVoyageNumber: portCall.voyageNumber2,
        portDisplayName: portCall.isInPortCluster ? portCall.port?.portClusterName ?? portCall.name : portCall.port.portName ?? portCall.name,
        portCallStatus: getPortCallStatusEnum(portCall.portCallStatus),
        departureDateTimeUtc:
            portCall.departureDate?.actualDateTimeUtc?.toISOString() ?? portCall.departureDate?.plannedDateTimeUtc?.toISOString() ?? '',
        arrivalDateTimeUtc: portCall.arrivalDate?.actualDateTimeUtc?.toISOString() ?? portCall.arrivalDate?.plannedDateTimeUtc?.toISOString() ?? '',
    } as RotationPortCall;

    rotationPortCall.berthCalls = portCall.berthCallArray?.map((bc) => mapBerthCallToRotationBerthCallSimple(rotationPortCall, bc)) ?? [];
    return rotationPortCall;
};

/**
 * Maps a RotationPortCall to a PortCall
 */
export const mapRotationPortCallToPortCall = (
    rotationPortCall: RotationPortCall,
    portNameList: PortName[],
    timeZoneArray: TimeZone[],
    previousPortCall?: PortCall,
    mergedOrcaPortCallIds?: Set<number>
): PortCall | null => {
    const portCallStatus = getPortCallStatus(rotationPortCall.portCallStatus);
    const voyageNumberString = getVoyageNumberString(rotationPortCall);
    const port = createPort(rotationPortCall, portNameList, timeZoneArray);
    const isInPortCluster = port.portClusterName !== null;

    const previousClusterName = previousPortCall?.port?.portClusterName;
    const isSameCluster = isInPortCluster && previousClusterName === port.portClusterName;

    const isSamePort = previousPortCall?.port?.portId === port.portId;

    const isTurnaroundContinuation =
        (isSameCluster || isSamePort) &&
        previousPortCall?.voyageNumber1 === rotationPortCall.voyageNumber &&
        rotationPortCall.turnaroundVoyageNumber !== undefined &&
        rotationPortCall.turnaroundVoyageNumber !== null;

    const isDuplicateTurnaround =
        (isSameCluster || isSamePort) &&
        previousPortCall?.voyageNumber1 === rotationPortCall.voyageNumber &&
        previousPortCall?.voyageNumber2 === rotationPortCall.turnaroundVoyageNumber &&
        rotationPortCall.turnaroundVoyageNumber !== undefined &&
        rotationPortCall.turnaroundVoyageNumber !== null;

    const isConsecutiveSamePort =
        isSamePort &&
        previousPortCall &&
        rotationPortCall.voyageNumber !== undefined &&
        previousPortCall.voyageNumber1 !== undefined &&
        +rotationPortCall.voyageNumber === +previousPortCall.voyageNumber1 + 1 &&
        !previousPortCall.voyageNumber2 && // Previous entry is a single-voyage entry
        rotationPortCall.turnaroundVoyageNumber !== undefined && // MUST have turnaround explicitly set
        rotationPortCall.turnaroundVoyageNumber !== null; // And not be null

    const wasAlreadyMerged = previousPortCall && mergedOrcaPortCallIds && mergedOrcaPortCallIds.has(previousPortCall.portCallId);

    // FIX: For same cluster merges, ignore wasAlreadyMerged check
    // Multiple ports in the same cluster should all merge into the same port call
    const shouldMerge =
        (isSameCluster && !isTurnaroundContinuation && !isDuplicateTurnaround && !isConsecutiveSamePort) ||
        ((isTurnaroundContinuation || isDuplicateTurnaround || isConsecutiveSamePort) && !wasAlreadyMerged);

    if (shouldMerge) {
        const berthCalls = mapRotationBerthCallsToBerthCalls(getBerthCalls(rotationPortCall) ?? [], voyageNumberString, previousPortCall);

        if (((isTurnaroundContinuation && !isDuplicateTurnaround) || isConsecutiveSamePort) && previousPortCall) {
            previousPortCall.berthCallArray = previousPortCall.berthCallArray.filter((bc) => bc.portClusterInitialPortId !== port.portId);
        }

        updateBerthCallInPortCluster(port.portId, port.portName, berthCalls);

        const existingBerthIds = new Set(previousPortCall.berthCallArray.map((bc) => bc.berthCallId));
        const newBerthCalls = berthCalls.filter((bc) => !existingBerthIds.has(bc.berthCallId));
        previousPortCall.berthCallArray = previousPortCall.berthCallArray.concat(newBerthCalls);

        if (previousPortCall.voyageNumber1 !== rotationPortCall.voyageNumber && !previousPortCall.voyageNumber2) {
            previousPortCall.voyageNumber2 = rotationPortCall.voyageNumber;
            previousPortCall.voyageNumberPresentationString = `${previousPortCall.voyageNumber1} / ${rotationPortCall.voyageNumber}`;
        }

        if (rotationPortCall.turnaroundVoyageNumber && !previousPortCall.voyageNumber2) {
            previousPortCall.voyageNumber2 = rotationPortCall.turnaroundVoyageNumber;
            previousPortCall.voyageNumberPresentationString = `${previousPortCall.voyageNumber1} / ${rotationPortCall.turnaroundVoyageNumber}`;
        }

        if (mergedOrcaPortCallIds && previousPortCall) {
            mergedOrcaPortCallIds.add(previousPortCall.portCallId);
        }

        return null;
    }
    const portCall = {
        portCallId: rotationPortCall.imosPortCallId,
        berthCallArray: [],
        id: rotationPortCall.imosPortCallId,
        voyageNumberPresentationString: voyageNumberString,
        voyageNumber1: rotationPortCall.voyageNumber,
        voyageNumber2: rotationPortCall.turnaroundVoyageNumber,
        name: isInPortCluster ? port.portClusterName.toUpperCase() : rotationPortCall.portDisplayName,
        // TODO: LOGIC-ERROR - departureDate uses arrivalDateTimeUtc for both parameters, should use departureDateTimeUtc
        arrivalDate: createEventDate(rotationPortCall.arrivalDateTimeUtc, rotationPortCall.arrivalDateTimeUtc),
        departureDate: createEventDate(rotationPortCall.arrivalDateTimeUtc, rotationPortCall.arrivalDateTimeUtc),
        isSelected: false,
        status: portCallStatus,
        portCallStatus: portCallStatus,
        isDeparted: portCallStatus === PortCallStatus.Departed,
        rotationTreeEnum: RotationTreeEnum.PortCall,
        type: RotationTreeEnum.PortCall,
        port: port,
        timeDeltaInSeconds: 0,
        nodeStatus: '',
        isDeleted: false,
        isMoved: false,
        isMovedTo: false,
        isLocked: false,
        isInPortCluster: isInPortCluster,
        deletedIconTooltipText: null,
        aolDate: null,
        reportLocation: ReportLocationEnum.Port,
        isAlreadyIncludedInAPortCluster: false, // This is a new port call, not already included
        movedFromClicked: false,
        imosPortFunction: null,
        timezoneWarningText: null,
        chemicalRobForPortCall: null,
        chemicalOrderForPortCallList: [],
        portClusterInitialPortId: isInPortCluster ? port.portId : null,
        portClusterInitialPortName: isInPortCluster ? port.portClusterName : null,
        delayInputWarningText: null,
        firstTimeInPortCall: null,
        actionMsg: emptyMoveActionMsg,
        portCall: null as any, // Will be set after creation
    };

    portCall.berthCallArray = mapRotationBerthCallsToBerthCalls(getBerthCalls(rotationPortCall) ?? [], voyageNumberString, portCall);
    updateBerthCallInPortCluster(portCall.port.portId, portCall.port.portName, portCall.berthCallArray);

    return portCall;
};

const createPort = (rotationPortCall: RotationPortCall, portNameList: PortName[], timeZoneArray: TimeZone[]): Port => {
    const portName = portNameList.find((pn) => pn.portId === rotationPortCall.portId);
    return {
        portId: rotationPortCall.portId,
        portName: rotationPortCall.portDisplayName,
        portClusterName: portName?.portClusterName || null,
        terminalArray: [],
        timeZoneArray: timeZoneArray,
        isEuMrvPort: false,
        country: null,
        deltaInSecondsOnMachine: null,
        pKPortId: null,
        pKBerthId: null,
        pKTerminalId: null,
        pKBerthRestrictionId: null,
        changedBy: null,
    };
};

const getPortCallStatus = (portCallStatus: PortCallStatusEnum): PortCallStatus => {
    switch (portCallStatus) {
        case PortCallStatusEnum.Departed:
            return PortCallStatus.Departed;
        case PortCallStatusEnum.Arrived:
            return PortCallStatus.Arrived;
        case PortCallStatusEnum.Planned:
            return PortCallStatus.Planned;
        default:
            return PortCallStatus.Hidden;
    }
};

const getPortCallStatusEnum = (portCallStatus: PortCallStatus): PortCallStatusEnum => {
    switch (portCallStatus) {
        case PortCallStatus.Departed:
            return PortCallStatusEnum.Departed;
        case PortCallStatus.Arrived:
            return PortCallStatusEnum.Arrived;
        case PortCallStatus.Planned:
            return PortCallStatusEnum.Planned;
        default:
            return PortCallStatusEnum.Hidden;
    }
};
